import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

import LoginBackground from './LoginBackground';
import './Login.css';


// Starfield Background Component (copied from home page)
const Starfield = () => {
  const canvasRef = useRef(null);
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let stars = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createStars = () => {
      stars = [];
      const numStars = isDarkMode ? 150 : 100;

      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 1.5 + 0.5,
          opacity: Math.random(),
          twinkleSpeed: Math.random() * 0.02 + 0.005,
          twinkleDirection: Math.random() > 0.5 ? 1 : -1,
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      stars.forEach((star) => {
        // Update twinkle
        star.opacity += star.twinkleSpeed * star.twinkleDirection;
        if (star.opacity >= 1 || star.opacity <= 0) {
          star.twinkleDirection *= -1;
        }

        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * (isDarkMode ? 0.8 : 0.6)})`;
        ctx.fill();

        // Add glow effect for dark mode
        if (isDarkMode) {
          ctx.shadowBlur = 10;
          ctx.shadowColor = 'rgba(255, 255, 255, 0.5)';
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      });

      animationId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createStars();
    animate();

    const handleResize = () => {
      resizeCanvas();
      createStars();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 -z-20 pointer-events-none"
      style={{ background: 'transparent' }}
    />
  );
};

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const { login, signInWithGoogle, isLoading } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();



  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const result = await login(formData.email, formData.password);

    if (result.success) {
      navigate('/preferences');
    } else {
      // Map Firebase auth errors to user-friendly messages
      let errorMessage = result.error;
      if (result.error.includes('auth/user-not-found')) {
        errorMessage = 'No account found with this email address.';
      } else if (result.error.includes('auth/wrong-password')) {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (result.error.includes('auth/invalid-email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (result.error.includes('auth/user-disabled')) {
        errorMessage = 'This account has been disabled.';
      } else if (result.error.includes('auth/too-many-requests')) {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      }
      setErrors({ general: errorMessage });
    }
  };

  const handleGoogleSignIn = async () => {
    const result = await signInWithGoogle();
    if (result.success) {
      navigate('/index');
    } else {
      setErrors({ general: 'Failed to sign in with Google. Please try again.' });
    }
  };

  const handleGuestLogin = () => {
    // For guest, just navigate to preferences without authentication
    navigate('/index');
  };

  return (
    <div className={`relative min-h-screen flex items-center justify-center transition-colors duration-200 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <LoginBackground />
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={toggleTheme}
          aria-label="Toggle theme"
          className="p-2 rounded-xl bg-pastel-neutral-100 dark:bg-pastel-neutral-800 text-pastel-neutral-600 dark:text-pastel-neutral-300 hover:bg-pastel-blue-100 dark:hover:bg-pastel-blue-900/30 hover:text-pastel-blue-600 dark:hover:text-pastel-blue-400 transition-all duration-300 shadow-soft"
        >
          {isDarkMode ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={`max-w-md w-full space-y-8 z-10 relative rounded-xl backdrop-blur-md p-8 shadow-lg ${isDarkMode ? 'bg-gray-800/80 text-white' : 'bg-white/20 text-pastel-neutral-900'}`}
      >
        <div>
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto h-16 w-16 bg-gradient-to-br from-pastel-blue-500 to-pastel-purple-600 rounded-2xl flex items-center justify-center shadow-glow"
          >
            <span className="text-white font-bold text-2xl">C</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-6 text-center text-3xl font-extrabold text-pastel-neutral-900 dark:text-white"
          >
            Welcome back
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-2 text-center text-sm text-pastel-neutral-600 dark:text-pastel-neutral-400"
          >
            Sign in to your Chillspace account
          </motion.p>
        </div>

        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 space-y-6"
          onSubmit={handleSubmit}
        >
          {errors.general && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm"
            >
              {errors.general}
            </motion.div>
          )}

          <div className="space-y-4">
            <div className="relative">
              <label htmlFor="email" className="block text-sm font-medium text-pastel-neutral-700 dark:text-pastel-neutral-300">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 block w-full pl-10 pr-3 py-2 border rounded-lg shadow-sm placeholder-pastel-neutral-400 focus:outline-none focus:ring-2 focus:ring-pastel-blue-500 focus:border-pastel-blue-500 transition-colors ${
                  errors.email
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-pastel-neutral-300 dark:border-pastel-neutral-600'
                } bg-white dark:bg-pastel-neutral-800 text-pastel-neutral-900 dark:text-white`}
                placeholder="Enter your email"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="h-5 w-5 text-pastel-neutral-400 dark:text-pastel-neutral-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.94 6.94a2 2 0 012.83 0L10 11.17l4.23-4.23a2 2 0 112.83 2.83l-6 6a2 2 0 01-2.83 0l-6-6a2 2 0 010-2.83z" />
                </svg>
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
              )}
            </div>

            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium text-pastel-neutral-700 dark:text-pastel-neutral-300">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 block w-full pl-10 pr-3 py-2 border rounded-lg shadow-sm placeholder-pastel-neutral-400 focus:outline-none focus:ring-2 focus:ring-pastel-blue-500 focus:border-pastel-blue-500 transition-colors ${
                  errors.password
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-pastel-neutral-300 dark:border-pastel-neutral-600'
                } bg-white dark:bg-pastel-neutral-800 text-pastel-neutral-900 dark:text-white`}
                placeholder="Enter your password"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="h-5 w-5 text-pastel-neutral-400 dark:text-pastel-neutral-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 8a3 3 0 116 0v1h1a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2v-3a2 2 0 012-2h1V8zm3-2a2 2 0 00-2 2v1h4V8a2 2 0 00-2-2z" clipRule="evenodd" />
                </svg>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm text-pastel-neutral-700 dark:text-pastel-neutral-300">
              <input type="checkbox" className="form-checkbox h-4 w-4 text-pastel-blue-500 rounded" />
              <span className="ml-2">Remember me</span>
            </label>
            <Link to="/forgot-password" className="text-sm text-pastel-blue-600 hover:text-pastel-blue-700 dark:text-pastel-blue-400 dark:hover:text-pastel-blue-300">
              Forgot password?
            </Link>
          </div>

          <div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pastel-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-glow ${
                isDarkMode ? 'text-white bg-pastel-blue-600 hover:bg-pastel-blue-700' : 'text-pastel-neutral-900 bg-white hover:bg-pastel-neutral-100'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-pastel-neutral-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </motion.button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDarkMode ? 'border-pastel-neutral-600' : 'border-pastel-neutral-300'}`} />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className={`px-2 ${isDarkMode ? 'bg-gray-900 text-pastel-neutral-400' : 'bg-white text-pastel-neutral-500'}`}>Or continue with</span>
            </div>
          </div>

          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex justify-center items-center px-4 py-3 border border-pastel-neutral-300 dark:border-pastel-neutral-600 rounded-lg shadow-sm bg-white dark:bg-pastel-neutral-800 text-sm font-medium text-pastel-neutral-700 dark:text-pastel-neutral-300 hover:bg-pastel-neutral-50 dark:hover:bg-pastel-neutral-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pastel-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M23.64 12.204c0-.639-.057-1.252-.162-1.837H12v3.481h6.844a5.862 5.862 0 01-2.54 3.846v3.197h4.107c2.4-2.213 3.778-5.462 3.778-9.687z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.963-1.073 7.951-2.91l-4.107-3.197c-1.14.766-2.6 1.22-3.844 1.22-2.956 0-5.462-1.995-6.356-4.677H1.44v2.94A11.998 11.998 0 0012 24z"/>
                <path fill="#FBBC05" d="M5.644 14.436a7.18 7.18 0 010-4.872V6.624H1.44a11.998 11.998 0 000 10.752l4.204-2.94z"/>
                <path fill="#EA4335" d="M12 4.77c1.76 0 3.344.606 4.59 1.796l3.44-3.44C17.96 1.367 15.24 0 12 0 7.2 0 3.12 2.88 1.44 6.624l4.204 3.02C6.538 6.765 9.012 4.77 12 4.77z"/>
              </svg>
              Sign in with Google
            </motion.button>
          </div>

          <div className={`text-center text-sm ${isDarkMode ? 'text-pastel-neutral-400' : 'text-pastel-neutral-600'}`}>
            <button
              type="button"
              onClick={handleGuestLogin}
              className="underline hover:text-pastel-blue-500 focus:outline-none focus:ring-2 focus:ring-pastel-blue-500 rounded"
            >
              Continue as Guest
            </button>
          </div>
        </motion.form>
      </motion.div>
    </div>
  );
}

export default Login;
